/**
 * Limit artırma cevabı — backend bazen camelCase bazen snake_case döndürür.
 */
function normalizeLimitIncreaseResultShape(raw) {
  if (!raw || typeof raw !== 'object') return raw;
  return {
    success: raw.success ?? raw.isSuccess ?? raw.is_success,
    statusText: raw.statusText ?? raw.status_text ?? raw.status ?? raw.state,
    message: raw.message ?? raw.msg ?? raw.reason,
    currentLimit: raw.currentLimit ?? raw.current_limit ?? null,
    newLimit: raw.newLimit ?? raw.new_limit ?? null,
    result: raw.result ?? raw.outcome ?? raw.decision,
  };
}

/**
 * analytics:event / activity-update gövdesinde eventData bazen data ile ayrı kökte gelir.
 * Feed'de tek `data` objesi kullanıldığı için eventData'yı içeri taşır.
 * limitIncreaseResult çoğu zaman yalnızca eventData içinde gelir; UI (Detayları Göster) için köke taşınır.
 */
export function normalizeActivityData(event) {
  if (!event || typeof event !== 'object') return {};
  const base = event.data && typeof event.data === 'object' ? { ...event.data } : {};
  const ev = event.eventData || event.EventData;
  if (ev && typeof ev === 'object' && !base.eventData) {
    base.eventData = ev;
  }

  const pickLimitRaw = (src) => {
    if (!src || typeof src !== 'object') return undefined;
    return src.limitIncreaseResult ?? src.limit_increase_result;
  };

  if (base.limitIncreaseResult == null) {
    const fromRoot = pickLimitRaw(event);
    const fromEv = pickLimitRaw(ev);
    const fromNested = pickLimitRaw(base.eventData);
    const raw = fromRoot ?? fromEv ?? fromNested;
    if (raw != null) {
      base.limitIncreaseResult = normalizeLimitIncreaseResultShape(raw);
    }
  } else if (typeof base.limitIncreaseResult === 'object') {
    base.limitIncreaseResult = normalizeLimitIncreaseResultShape(base.limitIncreaseResult);
  }

  return base;
}

/** Aktivite satırından / payload'dan kullanıcı kimliği (çoğunlukla TC) */
export function resolveActivityUserId(source) {
  if (!source || typeof source !== 'object') return null;

  const data = source.data && typeof source.data === 'object' && !source.eventType
    ? source.data
    : normalizeActivityData(source);
  const ev = data.eventData || data.EventData || {};

  const pick = (v) => {
    if (v === null || v === undefined) return '';
    const s = String(v).trim();
    return s && s !== '-' ? s : '';
  };

  const candidates = [
    source.userId,
    source.user,
    data.userId,
    data.tcNo,
    data.identityNum,
    data.customerId,
    ev.userId,
    ev.tcNo,
    ev.identityNum,
    data.requestData?.identityNum,
    data.requestData?.tcNo,
    ev.requestData?.identityNum,
    ev.requestData?.tcNo,
  ];

  for (const c of candidates) {
    const id = pick(c);
    if (id) return id;
  }
  return null;
}

/**
 * Geçerli ilk tarihi ISO string olarak döndürür.
 * GET /api/admin/dashboard/activities kayıtlarında alan adı: `createdAt` (ActivityTrackingService).
 * Socket/Redux tarafında çoğunlukla `timestamp` (ms veya ISO).
 */
export function resolveActivityTimestamp(source) {
  if (!source || typeof source !== 'object') return null;
  const tryParse = (v) => {
    const d = parseActivityDate(v);
    return d ? d.toISOString() : null;
  };
  const topKeys = [
    'createdAt',
    'created_at',
    'timestamp',
    'occurredAt',
    'eventTime',
    'time',
    'date',
    'updatedAt',
  ];
  for (const k of topKeys) {
    const iso = tryParse(source[k]);
    if (iso) return iso;
  }
  const nested = source.data && typeof source.data === 'object' ? source.data : null;
  if (nested) {
    for (const k of topKeys) {
      const iso = tryParse(nested[k]);
      if (iso) return iso;
    }
  }
  const ev = source.event && typeof source.event === 'object' ? source.event : null;
  if (ev) {
    for (const k of topKeys) {
      const iso = tryParse(ev[k]);
      if (iso) return iso;
    }
  }
  return null;
}

/**
 * Aktivite zamanını Date'e parse eder.
 * Bazı payload'larda UTC işaretli saatler ileri kayabiliyor; gelecekte görünen
 * zamanlar için timezone işaretini kaldırılmış adayla da dener.
 */
export function parseActivityDate(value) {
  if (value === undefined || value === null || value === '') return null;

  const direct = new Date(value);
  if (isNaN(direct.getTime())) return null;

  const isStringValue = typeof value === 'string';
  if (!isStringValue) return direct;

  const now = Date.now();
  const isFarFuture = direct.getTime() - now > 2 * 60 * 1000;
  if (!isFarFuture) return direct;

  const hasTimezoneSuffix = /([zZ]|[+-]\d{2}:?\d{2})$/.test(value);
  if (!hasTimezoneSuffix) return direct;

  const withoutTimezone = value.replace(/([zZ]|[+-]\d{2}:?\d{2})$/, '');
  const localCandidate = new Date(withoutTimezone);
  if (isNaN(localCandidate.getTime())) return direct;

  return localCandidate;
}
